import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Search, X, Check } from "lucide-react";
import type { ConnectionState } from "@timibot/core";
import { TimiMascot } from "./Mascot";
import "./components.css";

// 全 App 共用的小组件：对话、技能、工作台用同一套样子（对应 iOS 的 DesignSystem/Components.swift）。
// 规则：白底 + 浅灰圆角块，不用分割线；图标黑白；点一下打开、右键/长按出菜单。

export function SectionTitle({ title, trailing }: { title: string; trailing?: ReactNode }) {
  return (
    <div className="section-title">
      <span className="t-headline">{title}</span>
      <span className="spacer" />
      {trailing}
    </div>
  );
}

export function SearchField({ placeholder, value, onChange, onSubmit, autoFocus }: {
  placeholder: string; value: string; onChange: (v: string) => void; onSubmit?: () => void; autoFocus?: boolean;
}) {
  return (
    <label className="search-field">
      <Search size={17} className="t-3" />
      <input
        type="search"
        enterKeyHint="search"
        placeholder={placeholder}
        value={value}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") onSubmit?.(); }}
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
      />
      {value && (
        <button className="search-clear" aria-label="清除" onClick={() => onChange("")}>
          <X size={12} strokeWidth={3} />
        </button>
      )}
    </label>
  );
}

/** 两三个选项的切换（胶囊样式） */
export function PillSwitcher<T extends string>({ options, value, onChange }: {
  options: [T, string][]; value: T; onChange: (v: T) => void;
}) {
  return (
    <div className="pill-switcher" role="tablist">
      {options.map(([v, title]) => (
        <button key={v} role="tab" aria-selected={v === value} className={v === value ? "on" : ""} onClick={() => onChange(v)}>
          {title}
        </button>
      ))}
    </div>
  );
}

/** 黑白图标：浅灰圆底 + 深色符号 */
export function IconBadge({ icon, size = 32, dimmed, letter }: { icon?: ReactNode; size?: number; dimmed?: boolean; letter?: string }) {
  return (
    <span className={`icon-badge${dimmed ? " dimmed" : ""}`} style={{ width: size, height: size, fontSize: size * 0.42 }}>
      {letter ? <span className="semibold">{letter}</span> : icon}
    </span>
  );
}

/** 圆形图标按钮（新对话、新建自动化…） */
export function CircleIconButton({ icon, label, onClick, size = 32 }: { icon: ReactNode; label: string; onClick: () => void; size?: number }) {
  return (
    <button className="circle-button" style={{ width: size, height: size }} aria-label={label} title={label} onClick={onClick}>
      {icon}
    </button>
  );
}

/** 顶栏上的玻璃圆按钮（iOS 26 风格） */
export function GlassButton({ icon, label, onClick }: { icon: ReactNode; label: string; onClick: () => void }) {
  return (
    <button className="glass-button" aria-label={label} title={label} onClick={onClick}>
      {icon}
    </button>
  );
}

/** 小胶囊按钮（连接、安装、全部…） */
export function PillButton({ title, prominent, onClick, disabled, busy }: {
  title: ReactNode; prominent?: boolean; onClick: () => void; disabled?: boolean; busy?: boolean;
}) {
  return (
    <button className={`pill-button${prominent ? " prominent" : ""}`} onClick={onClick} disabled={disabled || busy}>
      {busy ? <span className={`spinner${prominent ? " light" : ""}`} style={{ width: 12, height: 12 }} /> : title}
    </button>
  );
}

export function PrimaryButton({ title, onClick, disabled, busy }: { title: string; onClick: () => void; disabled?: boolean; busy?: boolean }) {
  return (
    <button className="primary-button" onClick={onClick} disabled={disabled || busy}>
      {busy ? <span className="spinner light" /> : title}
    </button>
  );
}

/** 列表里的一行：图标 + 标题 + 一行说明 + 右侧内容。没有分割线，靠留白分隔。 */
export function PlainRow({ icon, title, subtitle, dimmed, letter, trailing, onClick, onContextMenu, danger }: {
  icon?: ReactNode; title: ReactNode; subtitle?: ReactNode; dimmed?: boolean; letter?: string; trailing?: ReactNode;
  onClick?: () => void; onContextMenu?: (e: React.MouseEvent) => void; danger?: boolean;
}) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag className={`plain-row${onClick ? " tappable" : ""}`} onClick={onClick} onContextMenu={onContextMenu}>
      {(icon || letter) && <IconBadge icon={icon} size={34} dimmed={dimmed} letter={letter} />}
      <span className="plain-row-text">
        <span className={`t-subheadline medium nowrap${dimmed ? " t-2" : ""}${danger ? " t-danger" : ""}`}>{title}</span>
        {subtitle ? <span className="t-caption t-2 nowrap">{subtitle}</span> : null}
      </span>
      {trailing}
    </Tag>
  );
}

export function StatusDot({ state }: { state: ConnectionState }) {
  const color = state.kind === "connected" ? "var(--success)"
    : state.kind === "connecting" || state.kind === "reconnecting" ? "var(--warning)"
    : state.kind === "needsLogin" ? "var(--danger)" : "var(--text-3)";
  return <span className="status-dot" style={{ background: color }} />;
}

/** Bot 头像：有图片用图片，没有用首字母 */
export function BotAvatar({ title, image, size = 28 }: { title: string; image?: string; size?: number }) {
  if (image) return <img className="bot-avatar" src={image} alt="" style={{ width: size, height: size }} />;
  return (
    <span className="bot-avatar letter" style={{ width: size, height: size, fontSize: size * 0.45 }}>
      {[...title][0]?.toUpperCase() ?? "?"}
    </span>
  );
}

export function Spinner({ size = 16 }: { size?: number }) {
  return <span className="spinner" style={{ width: size, height: size }} />;
}

/** 空状态：Timi + 一句话 */
export function EmptyState({ title, subtitle, mood = "idle", action }: { title: string; subtitle?: string; mood?: "idle" | "sleeping" | "thinking"; action?: ReactNode }) {
  return (
    <div className="empty-state">
      <TimiMascot size={64} mood={mood} />
      <div className="t-headline">{title}</div>
      {subtitle && <div className="t-subheadline t-2">{subtitle}</div>}
      {action}
    </div>
  );
}

// MARK: 弹出页

const sheetStack: (() => void)[] = [];

/**
 * 弹出页（对应 iOS 的 sheet）：手机上从下面滑上来、占满屏幕；电脑上是居中的卡片。
 * Esc 关闭；左上角“完成”。
 */
export function Sheet({ open, onClose, title, children, leading, trailing, size = "large", center }: {
  open: boolean; onClose: () => void; title?: ReactNode; children: ReactNode; leading?: ReactNode; trailing?: ReactNode;
  size?: "large" | "medium"; center?: ReactNode;
}) {
  const [closing, setClosing] = useState(false);
  const [mounted, setMounted] = useState(open);

  useEffect(() => {
    if (open) { setMounted(true); setClosing(false); }
    else if (mounted) {
      setClosing(true);
      const t = setTimeout(() => { setMounted(false); setClosing(false); }, 220);
      return () => clearTimeout(t);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const close = () => onClose();
    sheetStack.push(close);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && sheetStack[sheetStack.length - 1] === close) { e.preventDefault(); onClose(); }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      const i = sheetStack.indexOf(close);
      if (i >= 0) sheetStack.splice(i, 1);
    };
  }, [open, onClose]);

  if (!mounted) return null;
  return createPortal(
    <div className={`sheet-root${closing ? " closing" : ""}`}>
      <div className="sheet-scrim" onClick={onClose} />
      <div className={`sheet ${size}`} role="dialog" aria-modal="true">
        <div className="sheet-header">
          <div className="sheet-leading">{leading ?? <button className="sheet-done" onClick={onClose}>完成</button>}</div>
          <div className="sheet-title">{center ?? (title ? <span className="t-headline nowrap">{title}</span> : null)}</div>
          <div className="sheet-trailing">{trailing}</div>
        </div>
        <div className="sheet-body scroll">{children}</div>
      </div>
    </div>,
    document.body,
  );
}

// MARK: 菜单

export interface MenuItem {
  label: string;
  detail?: string;
  icon?: ReactNode;
  checked?: boolean;
  destructive?: boolean;
  disabled?: boolean;
  onSelect: () => void;
}

export type MenuEntry = MenuItem | { section: string } | "divider";

/** 下拉菜单（对应 iOS 的 Menu / contextMenu）。anchor 是触发它的元素或一个坐标。 */
export function Menu({ open, onClose, anchor, items, align = "center", width = 260 }: {
  open: boolean; onClose: () => void; anchor: HTMLElement | { x: number; y: number } | null; items: MenuEntry[];
  align?: "start" | "center" | "end"; width?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number; maxHeight: number } | null>(null);

  useLayoutEffect(() => {
    if (!open || !anchor) { setPos(null); return; }
    const rect = anchor instanceof HTMLElement ? anchor.getBoundingClientRect() : { left: anchor.x, right: anchor.x, top: anchor.y, bottom: anchor.y, width: 0 };
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = align === "start" ? rect.left : align === "end" ? rect.right - width : rect.left + rect.width / 2 - width / 2;
    left = Math.max(8, Math.min(left, vw - width - 8));
    const below = vh - rect.bottom - 12;
    const above = rect.top - 12;
    const height = ref.current?.scrollHeight ?? 300;
    const top = below >= Math.min(height, 320) || below >= above ? rect.bottom + 6 : Math.max(8, rect.top - 6 - Math.min(height, above));
    setPos({ left, top, maxHeight: Math.max(160, (top > rect.bottom ? below : above)) });
  }, [open, anchor, align, width]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, onClose]);

  if (!open) return null;
  return createPortal(
    <div className="menu-root" onClick={onClose} onContextMenu={(e) => { e.preventDefault(); onClose(); }}>
      <div
        ref={ref}
        className="menu"
        role="menu"
        style={{ width, left: pos?.left ?? -9999, top: pos?.top ?? -9999, maxHeight: pos?.maxHeight }}
        onClick={(e) => e.stopPropagation()}
      >
        {items.map((entry, i) => {
          if (entry === "divider") return <div key={i} className="menu-divider" />;
          if ("section" in entry) return <div key={i} className="menu-section t-caption t-2">{entry.section}</div>;
          return (
            <button
              key={i}
              role="menuitem"
              className={`menu-item${entry.destructive ? " destructive" : ""}`}
              disabled={entry.disabled}
              onClick={() => { onClose(); entry.onSelect(); }}
            >
              <span className="menu-check">{entry.checked ? <Check size={15} strokeWidth={2.5} /> : null}</span>
              <span className="menu-label">
                <span className="nowrap">{entry.label}</span>
                {entry.detail && <span className="t-caption t-2 nowrap">{entry.detail}</span>}
              </span>
              {entry.icon && <span className="menu-icon">{entry.icon}</span>}
            </button>
          );
        })}
      </div>
    </div>,
    document.body,
  );
}

/** 用一个按钮打开菜单 */
export function useMenu() {
  const [anchor, setAnchor] = useState<HTMLElement | { x: number; y: number } | null>(null);
  return {
    anchor,
    open: anchor !== null,
    show: (a: HTMLElement | { x: number; y: number }) => setAnchor(a),
    close: () => setAnchor(null),
  };
}

// MARK: 对话框

export interface DialogButton {
  title: string;
  role?: "cancel" | "destructive" | "default";
  onClick?: () => void;
}

/** 系统风格的提示框（对应 iOS 的 alert），可以带一个输入框 */
export function Dialog({ open, title, message, buttons, input, onClose }: {
  open: boolean; title: string; message?: string; buttons: DialogButton[];
  input?: { value: string; onChange: (v: string) => void; placeholder?: string; secure?: boolean };
  onClose: () => void;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.stopPropagation(); onClose(); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, onClose]);
  if (!open) return null;
  const primary = buttons.find((b) => b.role !== "cancel");
  return createPortal(
    <div className="dialog-root">
      <div className="dialog-scrim" onClick={onClose} />
      <div className="dialog" role="alertdialog" aria-modal="true">
        <div className="dialog-text">
          <div className="t-headline">{title}</div>
          {message && <div className="t-footnote t-2">{message}</div>}
          {input && (
            <input
              className="dialog-input"
              autoFocus
              type={input.secure ? "password" : "text"}
              value={input.value}
              placeholder={input.placeholder}
              onChange={(e) => input.onChange(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && primary) { onClose(); primary.onClick?.(); } }}
            />
          )}
        </div>
        <div className={`dialog-buttons${buttons.length > 2 ? " stacked" : ""}`}>
          {buttons.map((b, i) => (
            <button
              key={i}
              className={`dialog-button ${b.role ?? "default"}`}
              onClick={() => { onClose(); b.onClick?.(); }}
            >
              {b.title}
            </button>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** 长按（触屏）或右键（鼠标）打开菜单 */
export function useLongPress(onLongPress: (pos: { x: number; y: number }) => void, ms = 480) {
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const start = useRef<{ x: number; y: number } | null>(null);
  const fired = useRef(false);
  return {
    onContextMenu: (e: React.MouseEvent) => { e.preventDefault(); onLongPress({ x: e.clientX, y: e.clientY }); },
    onTouchStart: (e: React.TouchEvent) => {
      const t = e.touches[0];
      start.current = { x: t.clientX, y: t.clientY };
      fired.current = false;
      timer.current = setTimeout(() => { fired.current = true; onLongPress(start.current!); }, ms);
    },
    onTouchMove: (e: React.TouchEvent) => {
      const t = e.touches[0];
      if (start.current && Math.hypot(t.clientX - start.current.x, t.clientY - start.current.y) > 10) clearTimeout(timer.current);
    },
    onTouchEnd: (e: React.TouchEvent) => {
      clearTimeout(timer.current);
      if (fired.current) e.preventDefault();
    },
  };
}
